
import { Component, Mixins } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import UserCompaniesMixin from '@/mixins/UserCompaniesMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import SaveButton from '@/components/SaveButton.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import ProductSelector from '@/components/Order/ProductSelector.vue'
import QuantityTypeSelector from '@/components/Order/QuantityTypeSelector.vue'
import QuantityComboBox from '@/components/QuantityComboBox.vue'
import {
  CompanyCollection,
  CompanyTypeEnum,
  FavoriteList,
  FavoriteListCollection,
  IVendorPriceListEntry,
  MasterCatalogCollection,
  Par,
  ParCollection,
  PriceList,
  PriceListCollection,
  PriceListEntry
} from '@/firestore'

@Component({
  components: {
    SaveButton,
    LoadingPage,
    LabeledValue,
    ProductSelector,
    QuantityTypeSelector,
    QuantityComboBox
  }
})
export default class ParEdit extends Mixins(IconsMixin, AuthMixin, UserCompaniesMixin) {
  public isSaving: boolean = false
  public pars: Par[] = []
  public parsFromCollection: Par[] = []
  public priceListEntries: IVendorPriceListEntry[] = []
  public priceLists: PriceList[] = []
  public errors: string[] = []
  public showDeleteModal = false
  public showLocationDeleteModal = false
  public showAddLocationModal = false
  public showEditLocationModal = false
  public deactivatedPriceListID: string[] = []
  public parEntries: { [priceListEntryId: string]: IVendorPriceListEntry } = {
    ['']: {
      description: '',
      id: '',
      priceListId: '',
      upc: '',
      vendorCompanyId: ''
    }
  }

  public items = Array.from({length: 15}, (_, i) => i + 1)
  public selectedPar: Par = {} as Par
  public isLoading: boolean = false
  public displayHelperText: boolean = false
  public locations: Set<string> = new Set()
  public newLocationName: string = ''
  public selectedLocationName: string = ''
  public $refs!: {
    form: HTMLFormElement
  }

  public get displayedPars() {
    return this.pars.filter(par =>
            par
            && par.priceListEntryId === ''
            || (
                par.priceListEntryId
                && this.parEntries[par.priceListEntryId]
                && this.parEntries[par.priceListEntryId].id
                && this.parEntries[par.priceListEntryId].description
            )
    )
  }

  //METHODS
  public async mounted() {
    this.isLoading = true
    await this.populatePriceListEntries()
    const parCollection = new ParCollection((await this.$firebase).firestore())
    this.parsFromCollection = await parCollection.listParsByCompanyId(this.userCompany.id, CompanyTypeEnum.RESTAURANT)
    this.pars = [...this.parsFromCollection]

    this.pars.forEach(par => {
      this.parEntries[par.priceListEntryId] = this.getPriceListEntry(par.priceListEntryId)
      let priceList = this.priceLists.find(priceList => priceList.id == this.parEntries[par.priceListEntryId].priceListId)
      if (par.location) {
        this.locations.add(par.location)
      }

      if (!priceList || !priceList.active) {
        this.deactivatedPriceListID.push(this.parEntries[par.priceListEntryId].priceListId)
      }
    })

    if (!this.pars || !this.pars.length) {
      this.displayHelperText = true
      this.addPar('Main Bar')
      this.locations.add('Main Bar')
    }

    this.isLoading = false
  }

  public addLocation() {
    this.locations.add(this.newLocationName)
    this.newLocationName = ''
    this.showAddLocationModal = false
  }

  public addPar(location: string) {
    const par = new Par(this.userCompany.id, '', 1, location)
    this.pars.unshift(par)
  }

  public confirmDelete(par: Par) {
    this.showDeleteModal = false
    this.deletePar(par)
  }

  public async confirmLocationDelete() {
    const locationPars = this.pars.filter(par =>
        par.location === this.selectedLocationName
    )

    if (!locationPars || !locationPars.length) {
      return
    }

    const parCollection = new ParCollection((await this.$firebase).firestore())
    const operations = locationPars.map(async par => {
      await parCollection.delete(par)
    })

    await Promise.all(operations)

    this.locations.delete(this.selectedLocationName)

    this.showLocationDeleteModal = false
  }

  public async deletePar(par: Par) {
    if (!this.pars || !par) {
      return
    }

    try {
      const parCollection = new ParCollection((await this.$firebase).firestore())
      await parCollection.delete(par)
    } catch (err) {
      console.log(err)
    }
    this.removePar(par)
  }

  public removePar(par: Par) {
    if (!this.pars) {
      return
    }
    const index = this.pars.indexOf(par)
    if (index !== -1) {
      this.pars.splice(index, 1)
    }
  }

  public areParsValid(pars: Par[] | null): pars is Par[] {
    if (!pars) {
      return false
    }

    if (!this.$refs.form.validate()) {
      if (
          !pars.every(par => par.priceListEntryId && par.priceListEntryId.length)
      ) {
        this.errors.push('You must select an item for every Entry')
      }
      if (!pars.every(par => par.quantity && par.quantity > 0)) {
        this.errors.push('You must enter a number for every Entry')
      }
    }

    return !!this.$refs.form.validate()
        && pars.every(par => par instanceof Par)
        && !!pars
        && !!pars.length
        && !this.errors.length
  }

  public async updatePars() {
    if (this.areParsValid(this.pars)) {
      const parCollection = new ParCollection((await this.$firebase).firestore())

      const deDupedPars = this.displayedPars.reduce((acc, par) => {
        const isNewPar = !this.parsFromCollection.find(parFromCollection =>
            par.id === parFromCollection.id
        )

        if (isNewPar) {
          const preExistingPar = this.parsFromCollection.find(p =>
              p.priceListEntryId === par.priceListEntryId
              && p.quantityType === par.quantityType
              && p.location === par.location
          )

          if (preExistingPar) {
            preExistingPar.quantity += par.quantity
            return acc
          }
        }

        return [...acc, par]
      }, [] as Par[])

      const operations = deDupedPars.map(async par => {
        await parCollection.save(par)
      })

      await Promise.all(operations)
      await this.addMissingEntriesToFavoriteList()
    }
  }

  public async save() {
    this.errors = []
    await this.$nextTick()
    if (!this.areParsValid(this.pars)) {
      return
    }

    this.isSaving = true
    await this.updatePars()
    this.isSaving = false
    this.$router.go(-1)
  }

  public async saveEditLocation() {
    if (!this.newLocationName || !this.newLocationName.length) {
      return
    }

    const locationPars = this.pars.filter(par =>
        par.location === this.selectedLocationName
    )

    if (!locationPars || !locationPars.length) {
      return
    }

    const parCollection = new ParCollection((await this.$firebase).firestore())
    const operations = locationPars.map(async par => {
      par.location = this.newLocationName
      await parCollection.save(par)
    })

    await Promise.all(operations)

    this.locations.delete(this.selectedLocationName)
    this.locations.add(this.newLocationName)

    this.newLocationName = ''
    this.showEditLocationModal = false
  }

  public async addMissingEntriesToFavoriteList() {
    if (!this.pars || !this.pars.length) {
      return
    }

    const collection = new FavoriteListCollection((await this.$firebase).firestore())
    let originalFavoriteList = await collection.findFavoriteListByRestaurant(this.userCompany.id)

    if (!originalFavoriteList) {
      originalFavoriteList = new FavoriteList(this.userCompany.id)
    }

    const favoriteList = originalFavoriteList
    const originalFavoriteListEntriesLength = [...favoriteList.entries].length

    const operations = this.pars.map(async par => {
      const entry = this.parEntries[par.priceListEntryId]
      if (!entry || !entry.id || !entry.upc || !entry.description) {
        return
      }

      const entryFound = favoriteList.entries.find(e =>
          entry.id === e.id
      )

      if (!entryFound) {
        favoriteList.entries.push(
            {
              ...entry,
              vendorCompanyName: await this.getVendorName(entry)
            }
        )
      }
    })

    await Promise.all(operations)

    if (favoriteList.entries.length > originalFavoriteListEntriesLength) {
      await collection.save(favoriteList)
    }
  }

  public async getVendorName(entry: IVendorPriceListEntry) {
    if (!entry || !entry.vendorCompanyId) {
      return 'N/A'
    }

    const companyCollection = new CompanyCollection((await this.$firebase).firestore())
    const company = await companyCollection.getCompany(entry.vendorCompanyId)

    if (company) {
      return company.displayName
    }
  }

  public updateDetails(par: Par, priceListEntry: IVendorPriceListEntry) {
    par.priceListEntryId = priceListEntry.id
    this.parEntries[par.priceListEntryId] = this.getPriceListEntry(par.priceListEntryId)
  }

  public updateQuantity(par: Par, quantity: number) {
    par.quantity = quantity
  }

  public updateQuantityTypeSelection(par: Par, quantityType: any) {
    if (!par) {
      return
    }

    par.quantityType = quantityType
  }

  public editLocation(location: string) {
    if (!location || !location.length) {
      return
    }

    this.newLocationName = location
    this.selectedLocationName = location
    this.showEditLocationModal = true
  }

  public deleteLocation(location: string) {
    if (!location || !location.length) {
      return
    }

    this.selectedLocationName = location
    this.showLocationDeleteModal = true
  }

  public getPriceListEntry(priceListEntryId: string): IVendorPriceListEntry {
    const priceListEntry = this.priceListEntries.find(entry => entry.id === priceListEntryId)
    if (!priceListEntry) {
      console.log('priceListEntry not found: ', priceListEntryId)
      return {
        id: priceListEntryId,
        upc: '',
        description: '',
        priceListId: '',
        vendorCompanyId: ''
      } as IVendorPriceListEntry
    }
    return priceListEntry
  }

  private async populatePriceListEntries() {
    const priceListCollection = new PriceListCollection((await this.$firebase).firestore())
    this.priceLists = await priceListCollection.listPriceListsByCompanyId(this.userCompany.id, this.userCompany.type, true)
    const masterCatalogCollection = new MasterCatalogCollection((await this.$firebase).firestore())

    this.priceLists.forEach(async (priceList: PriceList) => {
      priceList.entries.forEach((entry: PriceListEntry) => {
        this.priceListEntries.push({...entry, priceListId: priceList.id, vendorCompanyId: priceList.vendorCompanyId})
      })
      const masterCatalog = await masterCatalogCollection.getByCompanyId(priceList.vendorCompanyId)
      if (masterCatalog && masterCatalog.entries && masterCatalog.entries.length) {
        masterCatalog.entries.forEach((entry: PriceListEntry) => {
          this.priceListEntries.push({...entry, priceListId: priceList.id, vendorCompanyId: masterCatalog.companyId})
        })
      }
    })
  }
}
